import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getTrialAccessStatus,
  setTrialAccess,
  TrialAccessStatus,
} from '@/apis/trialAccess';
import { useUserLogged } from '@/hooks/useUserLogged';
import headerObject from '@/types/headerObj';
import User from '@/types/userType';
import { useToast } from '@/hooks/useToast';

/** What the UI falls back to before the first response, and on any failure. */
const UNENFORCED: TrialAccessStatus = {
  Enforced: false,
  SeatCap: null,
  SeatsUsed: 0,
  IsPaid: false,
  InTrial: false,
  HasAccess: true,
  Role: '',
};

/**
 * Trial seat state for the current org and signed-in user.
 *
 * Returns `locked` as the single predicate every surface must gate on. The
 * sidebar filter and the seat gate both read it, so they cannot drift into
 * disagreeing about who is locked — and it encodes the contract that every
 * field other than `Enforced` is informational when enforcement is off. Reading
 * `HasAccess` alone would collapse the nav for an unenforced org that happened
 * to report `HasAccess: false`.
 *
 * `locked` is false while the query is in flight and after any transport error,
 * so a slow or failed lookup never strands someone who does hold a seat.
 */
export const useTrialAccessStatus = (
  organizationUUID: string | undefined,
  headerObj?: headerObject
) => {
  // Seat state is per person, so the cache key carries the user as well as the
  // org. Keyed on the org alone, a user switch inside a mounted dashboard would
  // serve the previous user's HasAccess and lock or unlock the wrong person.
  const { user } = useUserLogged();
  const userUUID = user?.UserUUID;

  const query = useQuery<TrialAccessStatus>({
    queryKey: ['trialAccess', organizationUUID, userUUID],
    queryFn: () => getTrialAccessStatus(organizationUUID as string, headerObj),
    enabled: !!organizationUUID && !!userUUID,
    staleTime: 30000,
    refetchOnWindowFocus: false,
  });

  const trialAccess = query.data ?? UNENFORCED;

  return {
    trialAccess,
    // Named `trialAccess`/`locked` rather than spread onto the query result:
    // react-query already owns `status`, and shadowing it would make the two
    // meanings indistinguishable at every call site.
    locked: trialAccess.Enforced && !trialAccess.HasAccess,
    isLoading: query.isLoading,
  };
};

/**
 * Grant or revoke a member's trial seat, with an optimistic row update.
 *
 * The mutation throws on a server refusal so the standard rollback path runs;
 * the thrown message is the backend's own user-facing copy, which is why a
 * `seat_cap_reached` refusal reads as "you've used all N seats" rather than a
 * generic failure.
 */
export const useSetTrialAccess = (
  organizationUUID: string | undefined,
  headerObj?: headerObject
) => {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: async ({ user, enabled }: { user: User; enabled: boolean }) => {
      const res = await setTrialAccess(
        {
          OrgUUID: organizationUUID || '',
          UserUUID: user.UserUUID,
          Enabled: enabled,
        },
        headerObj
      );
      if (!res.Success) {
        throw new Error(res.Error || 'Could not update trial access.');
      }
      return res;
    },
    onMutate: async ({ user, enabled }) => {
      await queryClient.cancelQueries({ queryKey: ['users'] });
      const previousUsers = queryClient.getQueryData([
        'users',
        'all',
        organizationUUID,
      ]);

      queryClient.setQueryData(
        ['users', 'all', organizationUUID],
        (old: User[] | undefined) => {
          if (!old) return old;
          return old.map((u) =>
            u.UserUUID === user.UserUUID ? { ...u, HasTrialAccess: enabled } : u
          );
        }
      );

      return { previousUsers };
    },
    onError: (error, _variables, context) => {
      if (context?.previousUsers) {
        queryClient.setQueryData(
          ['users', 'all', organizationUUID],
          context.previousUsers
        );
      }
      toast({
        title: 'Could not update trial access',
        description:
          error instanceof Error ? error.message : 'Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    },
    onSuccess: (_res, { enabled }) => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      // Paginated members table (Settings > Team) reads seat state per row.
      queryClient.invalidateQueries({ queryKey: ['orgMembers'] });
      // Prefix match, so it refreshes the entry for whichever user is signed in.
      queryClient.invalidateQueries({ queryKey: ['trialAccess'] });
      toast({
        title: enabled ? 'Trial access granted' : 'Trial access removed',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    },
  });
};
